module API.Signature where

import Common

import API.Types (DocumentID)
import GraphQL.Query (Mutation, Query, blobUpload, buildMutation, buildQuery, nameQuery)
import GraphQL.Type (NamedType, namedType)
import Internal.Luxon (LDateTime)
import Widgets.Form.Access (FormAccess)
import Widgets.Signature.Types (DocSigField, FormSignature, SigRelation, SignID, SignatureField, SignatureInfo, SignKey)


type SignResult =
    { id :: SignID
    , key :: SignKey
    , url :: String
    , created_at :: LDateTime
    , relationship :: SigRelation
    , contact :: SignatureInfo
    }

type SignOpts = { saveDefault :: Boolean }

-- | Email information pulled from the `verifications` relation in GraphQL
type SigVerification =
    { id :: SignID
    , created_at :: LDateTime
    , link :: String
    , data ::
        { key :: SignKey
        , email :: String
        , comment :: Maybe String
        }
    }

type BaseSigDefaults r =
    { key :: SignKey
    , default_email :: Maybe String
    , default_first_name :: Maybe String
    , default_last_name :: Maybe String
    | r
    }

type SigDefaults = BaseSigDefaults ()

type SignatureRefreshBase r =
    { signatures :: Array SignResult
    , verifications :: Array SigVerification
    , signature_fields :: Array DocSigField
    , locked_at :: Maybe LDateTime
    , access :: FormAccess
    , can :: { lock :: Boolean, update :: Boolean }
    | r
    }

type SignatureRefresh = SignatureRefreshBase ()

-- | Sends a request to the backend to sign the given form with the given form
-- | signature.
signForm :: DocumentID -> SignatureField -> FormSignature -> SignOpts -> Mutation SignResult
signForm docId { key } { first_name, last_name, email, signature } { saveDefault } =
    buildMutation "signForm"
    { id: docId
    , data: namedType
        { key
        , first_name
        , last_name
        , email
        , file: blobUpload signature
        , save_default: saveDefault
        } :: NamedType "SignFormInput" _
    }

-- | Sends a request to the backend to sign the given form with the given form
-- | signature.
signWithSaved :: DocumentID -> SignatureField -> Mutation SignResult
signWithSaved docId { key } = buildMutation "signForm"
    { id: docId
    , data: namedType { key, use_default: true } :: NamedType "SignFormInput" _
    }

fetchSignatureRefresh :: DocumentID -> Query SignatureRefresh
fetchSignatureRefresh docId = nameQuery "GetDocument" $
    buildQuery "form" { id: docId }


type SignatureRequest =
    { docId :: DocumentID
    , key :: SignKey
    , email :: String
    , comment :: String
    }

requestSigs :: Array SignatureRequest -> Mutation (Array SigVerification)
requestSigs reqs = buildMutation "requestSignature" { input: map mapReq reqs }
  where
    mapReq :: SignatureRequest -> NamedType "RequestSignatureInput" _
    mapReq { docId, email, comment, key } = namedType { form_id: docId, email, comment, key }
